function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.content || "";
}

function setEditing(editor, editing) {
  const display = editor.querySelector("[data-inline-display]");
  const start = editor.querySelector("[data-inline-start]");
  const form = editor.querySelector("[data-inline-form]");
  display.hidden = editing;
  start.hidden = editing;
  form.hidden = !editing;
  if (editing) {
    const input = editor.querySelector("[data-inline-input]");
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }
}

function updateDirty(editor) {
  const input = editor.querySelector("[data-inline-input]");
  const expected = editor.querySelector("[data-inline-expected]");
  editor.querySelector("[data-inline-save]").disabled = input.value === expected.value;
}

function updateHumanStamp(editor, iso, label) {
  const stamp = editor.querySelector("[data-human-updated]");
  stamp.hidden = false;
  stamp.textContent = "✓";
  stamp.tabIndex = 0;
  stamp.dataset.tooltip = `Human updated ${label}`;
  stamp.setAttribute("aria-label", `Human updated ${label}`);
  stamp.dataset.updatedAt = iso;
}

async function save(editor) {
  const form = editor.querySelector("[data-inline-form]");
  const input = editor.querySelector("[data-inline-input]");
  const expected = editor.querySelector("[data-inline-expected]");
  const saveButton = editor.querySelector("[data-inline-save]");
  const status = editor.querySelector("[data-inline-status]");

  saveButton.disabled = true;
  status.textContent = "Saving…";

  // A REQUEST THAT NEVER ARRIVES MUST STILL SAY SO. Without this the rejected
  // promise ended the function where it stood: the box sat on "Saving…" with
  // Save disabled and the typing still in it, and nothing ever came of it.
  // A dropped connection is not a saved edit, and the editor may not look like
  // it is still trying when it has stopped.
  let response;
  let data;
  try {
    response = await fetch(form.action, {
      method: "PATCH",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken()
      },
      body: JSON.stringify({ value: input.value, expected_value: expected.value })
    });
    data = await response.json();
  } catch (error) {
    status.textContent = "The change could not be saved — the connection failed. Your text is still here; try again.";
    status.dataset.error = "true";
    saveButton.disabled = false;
    return;
  }

  if (!response.ok) {
    status.textContent = data.error || "The change could not be saved.";
    status.dataset.error = "true";
    saveButton.disabled = false;
    return;
  }

  status.dataset.error = "false";
  status.textContent = "Saved.";
  expected.value = data.value;
  input.value = data.value;
  if (data.reload) {
    window.location.reload();
    return;
  }
  editor.querySelector("[data-inline-display]").innerHTML = data.html;
  updateHumanStamp(editor, data.humanUpdatedAt, data.humanUpdatedLabel);
  setEditing(editor, false);
}

function wire(editor) {
  if (editor.dataset.inlineReady === "true") return;
  editor.dataset.inlineReady = "true";

  const input = editor.querySelector("[data-inline-input]");
  if (!input) return;

  const display = editor.querySelector("[data-inline-display]");
  const beginEditing = () => setEditing(editor, true);

  editor.querySelector("[data-inline-start]").addEventListener("click", beginEditing);
  display.addEventListener("click", (event) => {
    // A citation or related-record link inside editable prose remains a link.
    // Clicking ordinary copy opens the field directly, which is the natural
    // interaction once site-wide edit mode is on.
    if (event.target.closest("a, button, input, textarea, select")) return;
    if (window.getSelection()?.toString()) return;
    beginEditing();
  });
  editor.querySelector("[data-inline-cancel]").addEventListener("click", () => {
    input.value = editor.querySelector("[data-inline-expected]").value;
    editor.querySelector("[data-inline-status]").textContent = "";
    setEditing(editor, false);
  });
  input.addEventListener("input", () => updateDirty(editor));
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      editor.querySelector("[data-inline-cancel]").click();
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      if (!editor.querySelector("[data-inline-save]").disabled) save(editor);
    }
  });
  editor.querySelector("[data-inline-form]").addEventListener("submit", (event) => {
    event.preventDefault();
    save(editor);
  });
}

let pageCopyPopover;

function closePageCopyEditor() {
  pageCopyPopover?.remove();
  pageCopyPopover = undefined;
  document.querySelectorAll("[data-page-copy-active]").forEach((element) => {
    delete element.dataset.pageCopyActive;
  });
}

function positionPageCopyEditor(popover, target) {
  const rect = target.getBoundingClientRect();
  const width = Math.min(620, Math.max(300, window.innerWidth - 24));
  const left = Math.min(Math.max(12, rect.left), window.innerWidth - width - 12);
  const roomBelow = window.innerHeight - rect.bottom;
  const top = roomBelow > 260 ? rect.bottom + 8 : Math.max(12, rect.top - 250);
  Object.assign(popover.style, { width: `${width}px`, left: `${left}px`, top: `${top}px` });
}

function openPageCopyEditor(target) {
  closePageCopyEditor();
  target.dataset.pageCopyActive = "true";

  const popover = document.createElement("form");
  popover.className = "page-copy-popover";
  popover.dataset.pageCopyPopover = "true";
  popover.setAttribute("aria-label", "Edit this page text");

  const heading = document.createElement("p");
  heading.className = "page-copy-popover__heading";
  heading.textContent = "Edit this text";
  const input = document.createElement("textarea");
  input.className = "inline-editor__input page-copy-popover__input";
  input.rows = 7;
  input.value = target.dataset.pageCopyValue || "";
  const expected = input.value;
  const actions = document.createElement("div");
  actions.className = "inline-editor__actions";
  const saveButton = document.createElement("button");
  saveButton.type = "submit";
  saveButton.className = "inline-editor__save";
  saveButton.textContent = "Save";
  saveButton.disabled = true;
  const cancelButton = document.createElement("button");
  cancelButton.type = "button";
  cancelButton.className = "inline-editor__cancel";
  cancelButton.textContent = "Cancel";
  const status = document.createElement("p");
  status.className = "inline-editor__status";
  status.setAttribute("aria-live", "polite");

  actions.append(saveButton, cancelButton);
  popover.append(heading, input, actions, status);
  document.body.appendChild(popover);
  pageCopyPopover = popover;
  positionPageCopyEditor(popover, target);
  input.focus();
  input.setSelectionRange(input.value.length, input.value.length);

  input.addEventListener("input", () => {
    saveButton.disabled = input.value === expected;
  });
  input.addEventListener("keydown", (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closePageCopyEditor();
    }
    if (event.key === "Enter" && (event.metaKey || event.ctrlKey) && !saveButton.disabled) {
      event.preventDefault();
      popover.requestSubmit();
    }
  });
  cancelButton.addEventListener("click", closePageCopyEditor);
  popover.addEventListener("submit", async (event) => {
    event.preventDefault();
    saveButton.disabled = true;
    status.textContent = "Saving…";

    // Same bargain as the model-backed editor above: say so rather than sit
    // on "Saving…" for ever.
    let response;
    let data;
    try {
      response = await fetch("/admin/page-copy", {
        method: "PATCH",
        headers: {
          "Accept": "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken()
        },
        body: JSON.stringify({
          key: target.dataset.pageCopyKey,
          scope: target.dataset.pageCopyScope,
          locator: target.dataset.pageCopyLocator,
          format: target.dataset.pageCopyFormat,
          seeded_body: target.dataset.pageCopySeeded,
          expected_value: expected,
          value: input.value
        })
      });
      data = await response.json();
    } catch (error) {
      status.textContent = "The change could not be saved — the connection failed. Your text is still here; try again.";
      status.dataset.error = "true";
      saveButton.disabled = false;
      return;
    }
    if (!response.ok) {
      status.textContent = data.error || "The change could not be saved.";
      status.dataset.error = "true";
      saveButton.disabled = false;
      return;
    }
    window.location.reload();
  });
}

function wirePageCopyFallback() {
  if (document.documentElement.dataset.pageCopyReady === "true") return;
  document.documentElement.dataset.pageCopyReady = "true";

  document.addEventListener("click", (event) => {
    if (event.target.closest("[data-page-copy-popover], [data-inline-editor], .public-admin-bar")) return;
    const target = event.target.closest("[data-page-copy-key]");
    if (!target || window.getSelection()?.toString()) return;

    event.preventDefault();
    event.stopPropagation();
    openPageCopyEditor(target);
  }, true);
  window.addEventListener("resize", () => {
    const target = document.querySelector("[data-page-copy-active]");
    if (pageCopyPopover && target) positionPageCopyEditor(pageCopyPopover, target);
  });
}

export function start() {
  document.querySelectorAll("[data-inline-editor]").forEach(wire);
  wirePageCopyFallback();
}

document.addEventListener("DOMContentLoaded", start);
document.addEventListener("turbo:load", start);
